import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useOfflineStorageV2 } from './useOfflineStorageV2';
import { logger } from '@/shared/utils/logger';

// ========== Types ==========

export type PreloadStage =
  | 'idle'
  | 'ra_profiles'
  | 'shop_products'
  | 'shop_deliveries'
  | 'shop_returns'
  | 'laundry_movements'
  | 'complete'
  | 'error';

export interface PreloadProgress {
  stage: PreloadStage;
  current: number;
  total: number;
  message: string;
  error?: string;
}

interface PreloadConfig {
  deliveryHistoryDays: number;
  laundryHistoryDays: number;
  debounceMs: number; // Debounce per registrazioni ravvicinate
  batchSize: number; // Max profili per batch RPC
}

interface PreloadMetadata {
  lastPreloadTime: number;
  raProfilesCount: number;
  shopProductsCount: number;
  shopDeliveriesCount: number;
  shopReturnsCount: number;
  laundryMovementsCount: number;
}

const DEFAULT_CONFIG: PreloadConfig = {
  deliveryHistoryDays: 14,
  laundryHistoryDays: 14,
  debounceMs: 2 * 60 * 1000, // 2 minuti
  batchSize: 50,
};

// ========== Hook ==========

/**
 * @description Preloads all application data (RA profiles, shop products, deliveries, returns, laundry) into IndexedDB for offline use with progress tracking and debounced triggers.
 * @returns Preload progress, start/trigger/cancel actions, and online/initialized status.
 */
export const useOfflinePreloader = (config: Partial<PreloadConfig> = {}) => {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };

  const [progress, setProgress] = useState<PreloadProgress>({
    stage: 'idle',
    current: 0,
    total: 0,
    message: '',
  });

  const [lastPreloadTime, setLastPreloadTime] = useState<Date | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingPreloadRef = useRef<boolean>(false);

  const {
    isOnline,
    isInitialized,
    cacheRAProfiles,
    cacheShopProducts,
    cacheShopDeliveries,
    cacheShopReturns,
    cacheLaundryMovements,
    updatePreloadMetadata,
    getPreloadMetadata,
  } = useOfflineStorageV2();

  // ========== Progress Helper ==========

  const updateProgress = useCallback((update: Partial<PreloadProgress>) => {
    setProgress(prev => ({ ...prev, ...update }));
  }, []);

  // ========== Preload Functions ==========

  /**
   * Pre-carica tutti i profili RA attivi (decriptati)
   */
  const preloadRAProfiles = useCallback(async (): Promise<number> => {
    updateProgress({ stage: 'ra_profiles', message: 'Caricamento profili RA...', current: 0, total: 0 });

    // Step 1: Get all active profile IDs
    const { data: profileIds, error: idsError } = await supabase
      .from('ra_profiles')
      .select('id')
      .neq('status', 'uscito');

    if (idsError) {
      logger.error('[useOfflinePreloader] Error fetching RA profile IDs:', idsError);
      throw idsError;
    }

    if (!profileIds?.length) {
      updateProgress({ current: 0, total: 0 });
      return 0;
    }

    const ids = profileIds.map(p => p.id);
    updateProgress({ total: ids.length });

    // Step 2: Decrypt in batches
    const allProfiles: any[] = [];

    for (let i = 0; i < ids.length; i += mergedConfig.batchSize) {
      if (abortControllerRef.current?.signal.aborted) {
        throw new Error('Preload cancelled');
      }

      const batchIds = ids.slice(i, i + mergedConfig.batchSize);

      const { data: decryptedBatch, error: rpcError } = await supabase
        .rpc('rpc_decrypt_ra_profiles', { profile_ids: batchIds });

      if (rpcError) {
        logger.error('[useOfflinePreloader] RPC decrypt error:', rpcError);
        throw rpcError;
      }

      if (decryptedBatch) {
        allProfiles.push(...decryptedBatch);
      }

      updateProgress({ current: Math.min(i + mergedConfig.batchSize, ids.length) });
    }

    // Step 3: Save to IndexedDB
    await cacheRAProfiles(allProfiles);

    logger.log(`[useOfflinePreloader] Cached ${allProfiles.length} RA profiles`);
    return allProfiles.length;
  }, [mergedConfig.batchSize, updateProgress, cacheRAProfiles]);

  /**
   * Pre-carica il catalogo prodotti shop
   */
  const preloadShopProducts = useCallback(async (): Promise<number> => {
    updateProgress({ stage: 'shop_products', message: 'Caricamento catalogo prodotti...', current: 0, total: 2 });

    // Load categories
    const { data: categories, error: catError } = await supabase
      .from('shop_product_categories')
      .select('*')
      .order('display_order');

    if (catError) {
      logger.error('[useOfflinePreloader] Error fetching categories:', catError);
      throw catError;
    }

    updateProgress({ current: 1 });

    // Load active products
    const { data: products, error: prodError } = await supabase
      .from('shop_products')
      .select('*')
      .eq('active', true)
      .order('display_order');

    if (prodError) {
      logger.error('[useOfflinePreloader] Error fetching products:', prodError);
      throw prodError;
    }

    updateProgress({ current: 2 });

    // Save to IndexedDB
    await cacheShopProducts({
      categories: categories || [],
      products: products || [],
    });

    const totalCount = (products?.length || 0);
    logger.log(`[useOfflinePreloader] Cached ${totalCount} shop products`);
    return totalCount;
  }, [updateProgress, cacheShopProducts]);

  /**
   * Pre-carica lo storico consegne shop (ultimi N giorni)
   */
  const preloadShopDeliveries = useCallback(async (): Promise<number> => {
    updateProgress({ stage: 'shop_deliveries', message: 'Caricamento storico consegne...', current: 0, total: 1 });

    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - mergedConfig.deliveryHistoryDays);

    const { data: deliveries, error } = await supabase
      .from('shop_product_deliveries')
      .select(`
        id,
        ra_profile_id,
        delivered_by,
        delivery_date,
        notes,
        stato,
        shop_product_delivery_items (
          id,
          product_id,
          quantity,
          product:shop_products (id, name, category_id, must_be_returned, is_dangerous)
        )
      `)
      .gte('delivery_date', fromDate.toISOString())
      .order('delivery_date', { ascending: false });

    if (error) {
      logger.error('[useOfflinePreloader] Error fetching deliveries:', error);
      throw error;
    }

    updateProgress({ current: 1, total: 1 });

    // Save to IndexedDB
    await cacheShopDeliveries(deliveries || []);

    const count = deliveries?.length || 0;
    logger.log(`[useOfflinePreloader] Cached ${count} shop deliveries`);
    return count;
  }, [mergedConfig.deliveryHistoryDays, updateProgress, cacheShopDeliveries]);

  /**
   * Pre-carica lo storico resi shop (ultimi N giorni)
   */
  const preloadShopReturns = useCallback(async (): Promise<number> => {
    updateProgress({ stage: 'shop_returns', message: 'Caricamento storico resi...', current: 0, total: 1 });

    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - mergedConfig.deliveryHistoryDays); // Use same history days as deliveries

    const { data: returns, error } = await supabase
      .from('shop_product_returns')
      .select('id, ra_profile_id, product_id, quantity, returned_by, returned_at, notes')
      .gte('returned_at', fromDate.toISOString())
      .order('returned_at', { ascending: false });

    if (error) {
      logger.error('[useOfflinePreloader] Error fetching returns:', error);
      throw error;
    }

    updateProgress({ current: 1, total: 1 });

    // Save to IndexedDB
    await cacheShopReturns(returns || []);

    const count = returns?.length || 0;
    logger.log(`[useOfflinePreloader] Cached ${count} shop returns`);
    return count;
  }, [mergedConfig.deliveryHistoryDays, updateProgress, cacheShopReturns]);

  /**
   * Pre-carica lo storico movimenti lavanderia (ultimi N giorni)
   */
  const preloadLaundryMovements = useCallback(async (): Promise<number> => {
    updateProgress({ stage: 'laundry_movements', message: 'Caricamento movimenti lavanderia...', current: 0, total: 1 });

    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - mergedConfig.laundryHistoryDays);

    const { data: movements, error } = await supabase
      .from('laundry_net_movements')
      .select(`
        *,
        net:laundry_nets (
          numero_rete,
          ra_profile_id,
          stato
        )
      `)
      .gte('data_movimento', fromDate.toISOString())
      .order('data_movimento', { ascending: false });

    if (error) {
      logger.error('[useOfflinePreloader] Error fetching laundry movements:', error);
      throw error;
    }

    updateProgress({ current: 1, total: 1 });

    // Save to IndexedDB
    await cacheLaundryMovements(movements || []);

    const count = movements?.length || 0;
    logger.log(`[useOfflinePreloader] Cached ${count} laundry movements`);
    return count;
  }, [mergedConfig.laundryHistoryDays, updateProgress, cacheLaundryMovements]);

  // ========== Main Preload Function ==========

  const startPreload = useCallback(async () => {
    if (!isOnline) {
      logger.warn('[useOfflinePreloader] Cannot preload while offline');
      return;
    }

    if (!isInitialized) {
      logger.warn('[useOfflinePreloader] Database not initialized');
      return;
    }

    if (progress.stage !== 'idle' && progress.stage !== 'complete' && progress.stage !== 'error') {
      logger.warn('[useOfflinePreloader] Preload already in progress');
      return;
    }

    abortControllerRef.current = new AbortController();

    try {
      let raCount = 0, shopCount = 0, deliveriesCount = 0, laundryCount = 0;

      // 1. RA Profiles
      raCount = await preloadRAProfiles();
      if (abortControllerRef.current?.signal.aborted) return;

      // 2. Shop Products
      shopCount = await preloadShopProducts();
      if (abortControllerRef.current?.signal.aborted) return;

      // 3. Shop Deliveries
      deliveriesCount = await preloadShopDeliveries();
      if (abortControllerRef.current?.signal.aborted) return;

      // 4. Shop Returns
      const returnsCount = await preloadShopReturns();
      if (abortControllerRef.current?.signal.aborted) return;

      // 5. Laundry Movements
      laundryCount = await preloadLaundryMovements();

      // Update metadata
      const metadata: PreloadMetadata = {
        lastPreloadTime: Date.now(),
        raProfilesCount: raCount,
        shopProductsCount: shopCount,
        shopDeliveriesCount: deliveriesCount,
        shopReturnsCount: returnsCount,
        laundryMovementsCount: laundryCount,
      };
      await updatePreloadMetadata(metadata);

      // Complete
      updateProgress({ stage: 'complete', message: 'Dati offline pronti!' });
      setLastPreloadTime(new Date());

      logger.log('[useOfflinePreloader] Preload completed:', metadata);

    } catch (error: any) {
      if (error.message === 'Preload cancelled') {
        updateProgress({ stage: 'idle', message: 'Pre-caricamento annullato' });
      } else {
        logger.error('[useOfflinePreloader] Preload failed:', error);
        updateProgress({
          stage: 'error',
          message: 'Errore durante il pre-caricamento',
          error: error.message,
        });
      }
    }
  }, [
    isOnline,
    isInitialized,
    progress.stage,
    preloadRAProfiles,
    preloadShopProducts,
    preloadShopDeliveries,
    preloadShopReturns,
    preloadLaundryMovements,
    updatePreloadMetadata,
    updateProgress,
  ]);

  /**
   * Trigger preload con debounce (per registrazioni ravvicinate)
   */
  const triggerPreload = useCallback(() => {
    if (!isOnline) return;

    // Se c'e' gia' un timer, resetta
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Segna che c'e' un preload pendente
    pendingPreloadRef.current = true;

    // Avvia timer
    debounceTimerRef.current = setTimeout(() => {
      if (pendingPreloadRef.current) {
        pendingPreloadRef.current = false;
        startPreload();
      }
    }, mergedConfig.debounceMs);
  }, [isOnline, mergedConfig.debounceMs, startPreload]);

  /**
   * Annulla il preload in corso
   */
  const cancelPreload = useCallback(() => {
    abortControllerRef.current?.abort();
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    pendingPreloadRef.current = false;
    updateProgress({ stage: 'idle', message: 'Pre-caricamento annullato' });
  }, [updateProgress]);

  // ========== Load metadata on init ==========

  useEffect(() => {
    if (isInitialized) {
      getPreloadMetadata().then((metadata) => {
        if (metadata?.lastPreloadTime) {
          setLastPreloadTime(new Date(metadata.lastPreloadTime));
        }
      });
    }
  }, [isInitialized, getPreloadMetadata]);

  // ========== Cleanup ==========

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // ========== Return ==========

  const isPreloading = ['ra_profiles', 'shop_products', 'shop_deliveries', 'shop_returns', 'laundry_movements'].includes(progress.stage);

  return {
    // State
    progress,
    isPreloading,
    lastPreloadTime,
    isOnline,
    isInitialized,

    // Actions
    startPreload,      // Immediate start
    triggerPreload,    // Debounced start (for after registrations)
    cancelPreload,
  };
};

export default useOfflinePreloader;
